import { useState, useEffect } from 'react';
import { builderApi } from '../api';
import { fmtDate, DateInput } from '../utils';

const PAYMENT_MODES = ['Cash', 'Bank Transfer', 'UPI', 'Cheque', 'Other'];
const PAY_TYPES     = ['Wages', 'Advance', 'Bonus', 'Overtime', 'Final Settlement', 'Other'];

const EMPTY_EMP = {
  employee_id: '', employee_name: '', project_id: '', project_name: '',
  payment_date: new Date().toISOString().slice(0, 10),
  amount: '', payment_mode: 'Cash', pay_type: 'Wages',
  trade: '', period_from: '', period_to: '', notes: '',
};

const EMPTY_SUP = {
  supplier: '', invoice_no: '', purchase_id: '',
  payment_date: new Date().toISOString().slice(0, 10),
  paid_amount: '', credit_amount: '', payment_mode: 'Cash', notes: '',
};

const fmt = n => n != null ? `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '₹0.00';

const MODE_COLORS = { Cash: '#38a169', 'Bank Transfer': '#3182ce', UPI: '#9f7aea', Cheque: '#d69e2e', Other: '#718096' };
const TYPE_COLORS = { Wages: '#3182ce', Advance: '#d69e2e', Bonus: '#38a169', Overtime: '#9f7aea', 'Final Settlement': '#e53e3e', Other: '#718096' };

function ErrBanner({ msg, onClose }) {
  if (!msg) return null;
  return (
    <div style={{ background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 8,
      padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 18 }}>⚠️</span>
      <span style={{ color: '#c53030', fontSize: 13, fontWeight: 600, flex: 1 }}>{msg}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#c53030', cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
    </div>
  );
}

// ─── Employee / Worker Payments tab ──────────────────────────────────────────
function EmployeePaymentsTab({ isOwner }) {
  const [payments, setPayments]   = useState([]);
  const [summary,  setSummary]    = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects,  setProjects]  = useState([]);
  const [form,  setForm]          = useState(EMPTY_EMP);
  const [showForm, setShowForm]   = useState(false);
  const [subTab,   setSubTab]     = useState('history');
  const [filterEmp,  setFilterEmp]  = useState('');
  const [filterProj, setFilterProj] = useState('');
  const [fromDate, setFromDate]   = useState('');
  const [toDate,   setToDate]     = useState('');
  const [loading,  setLoading]    = useState(true);
  const [err,      setErr]        = useState('');
  const [fe,       setFe]         = useState({});

  const inv = k => fe[k] ? { borderColor: '#e53e3e', background: '#fff5f5' } : {};

  const load = () => {
    setLoading(true);
    const params = {};
    if (filterEmp)  params.employeeId = filterEmp;
    if (filterProj) params.projectId  = filterProj;
    if (fromDate)   params.from       = fromDate;
    if (toDate)     params.to         = toDate;
    Promise.all([
      builderApi.getPayments(params),
      builderApi.getPaymentSummary(),
      builderApi.getEmployees(),
      builderApi.getProjects(),
    ]).then(([p, s, e, pr]) => { setPayments(p); setSummary(s); setEmployees(e); setProjects(pr); })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const pickEmployee = id => {
    const emp = employees.find(e => String(e.id) === String(id));
    setForm(f => ({ ...f, employee_id: id, employee_name: emp?.name || '', trade: emp?.trade || f.trade }));
  };
  const pickProject = id => {
    const pr = projects.find(p => String(p.id) === String(id));
    setForm(f => ({ ...f, project_id: id, project_name: pr?.name || '' }));
  };
  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    const errors = {};
    if (!form.employee_name.trim()) errors.employee_name = true;
    if (!form.amount || Number(form.amount) <= 0) errors.amount = true;
    if (!form.payment_date) errors.payment_date = true;
    setFe(errors);
    if (Object.keys(errors).length > 0) { setErr('Please fill in all required fields (marked *)'); return; }
    try {
      await builderApi.createPayment(form);
      setShowForm(false); setForm(EMPTY_EMP); setErr(''); setFe({}); load();
    } catch (e) { setErr(e.message); }
  };

  const del = async id => {
    if (!window.confirm('Delete this payment record?')) return;
    try { await builderApi.deletePayment(id); load(); } catch (e) { setErr(e.message); }
  };

  const totalPaid = payments.reduce((s, p) => s + Number(p.amount || 0), 0);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button className="btn btn-primary" onClick={() => { setForm(EMPTY_EMP); setShowForm(s => !s); }}>
          + Record Payment
        </button>
      </div>

      <ErrBanner msg={err} onClose={() => setErr('')} />

      {showForm && (
        <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', marginBottom: 12 }}>New Worker Payment</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <div>
              <label className="form-label">Employee *</label>
              <select className="form-input" style={inv('employee_name')} value={form.employee_id}
                onChange={e => { pickEmployee(e.target.value); if (fe.employee_name) setFe(p => ({ ...p, employee_name: false })); }}>
                <option value="">Select employee</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.name}{e.trade ? ` (${e.trade})` : ''}</option>)}
              </select>
              {!form.employee_id && (
                <input className="form-input" style={{ marginTop: 6, ...inv('employee_name') }} value={form.employee_name}
                  onChange={e => { f('employee_name', e.target.value); if (fe.employee_name) setFe(p => ({ ...p, employee_name: false })); }}
                  placeholder="Or type name manually" />
              )}
              {fe.employee_name && <div style={{ color: '#e53e3e', fontSize: 11, marginTop: 3 }}>Required</div>}
            </div>
            <div>
              <label className="form-label">Project</label>
              <select className="form-input" value={form.project_id} onChange={e => pickProject(e.target.value)}>
                <option value="">No project / General</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Payment Date *</label>
              <DateInput className="form-input" style={inv('payment_date')} value={form.payment_date}
                onChange={e => { f('payment_date', e.target.value); if (fe.payment_date) setFe(p => ({ ...p, payment_date: false })); }} />
            </div>
            <div>
              <label className="form-label">Amount (₹) *</label>
              <input className="form-input" style={inv('amount')} type="number" min="1" step="0.01" value={form.amount}
                onChange={e => { f('amount', e.target.value); if (fe.amount) setFe(p => ({ ...p, amount: false })); }} placeholder="0.00" />
            </div>
            <div>
              <label className="form-label">Payment Mode</label>
              <select className="form-input" value={form.payment_mode} onChange={e => f('payment_mode', e.target.value)}>
                {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Pay Type</label>
              <select className="form-input" value={form.pay_type} onChange={e => f('pay_type', e.target.value)}>
                {PAY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Trade</label>
              <input className="form-input" value={form.trade} onChange={e => f('trade', e.target.value)} placeholder="e.g. Plumber, Mason" />
            </div>
            <div>
              <label className="form-label">Period From</label>
              <DateInput className="form-input" value={form.period_from} onChange={e => f('period_from', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Period To</label>
              <DateInput className="form-input" value={form.period_to} onChange={e => f('period_to', e.target.value)} />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Notes</label>
              <input className="form-input" value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Reference, remarks…" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-primary" onClick={save}>Save Payment</button>
            <button className="btn btn-outline" onClick={() => { setShowForm(false); setErr(''); }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border-color)', marginBottom: 16 }}>
        {[{ key: 'history', label: 'Payment History' }, { key: 'summary', label: 'Employee Summary' }].map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '8px 20px', fontSize: 13, fontWeight: 600,
            color: subTab === t.key ? '#3182ce' : '#718096',
            borderBottom: subTab === t.key ? '2px solid #3182ce' : '2px solid transparent', marginBottom: -2,
          }}>{t.label}</button>
        ))}
      </div>

      {subTab === 'history' && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label className="form-label">Employee</label>
            <select className="form-input" style={{ minWidth: 160 }} value={filterEmp} onChange={e => setFilterEmp(e.target.value)}>
              <option value="">All Employees</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Project</label>
            <select className="form-input" style={{ minWidth: 140 }} value={filterProj} onChange={e => setFilterProj(e.target.value)}>
              <option value="">All Projects</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div><label className="form-label">From</label><DateInput className="form-input" value={fromDate} onChange={e => setFromDate(e.target.value)} /></div>
          <div><label className="form-label">To</label><DateInput className="form-input" value={toDate} onChange={e => setToDate(e.target.value)} /></div>
          <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={load}>Apply</button>
          <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={() => { setFilterEmp(''); setFilterProj(''); setFromDate(''); setToDate(''); }}>Clear</button>
        </div>
      )}

      {loading && <div style={{ color: '#718096', fontSize: 13, padding: 12 }}>Loading…</div>}

      {subTab === 'history' && !loading && (
        <>
          {payments.length > 0 && (
            <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: '10px 16px', marginBottom: 12,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#4a5568' }}>{payments.length} payment(s)</span>
              <span style={{ fontWeight: 700, fontSize: 16, color: '#38a169' }}>Total: {fmt(totalPaid)}</span>
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th><th>Employee</th><th>Trade</th><th>Project</th>
                  <th>Pay Type</th><th>Mode</th><th>Period</th>
                  <th style={{ textAlign: 'right' }}>Amount</th><th>Notes</th><th></th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 && (
                  <tr><td colSpan={10} style={{ textAlign: 'center', color: '#718096', padding: 24 }}>No payments recorded yet</td></tr>
                )}
                {payments.map(p => (
                  <tr key={p.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(p.payment_date)}</td>
                    <td style={{ fontWeight: 600 }}>{p.employee_name}</td>
                    <td>{p.trade || <span style={{ color: '#718096' }}>—</span>}</td>
                    <td>{p.project_name || <span style={{ color: '#718096' }}>—</span>}</td>
                    <td>
                      <span style={{ background: TYPE_COLORS[p.pay_type] || '#718096', color: '#fff',
                        padding: '2px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{p.pay_type}</span>
                    </td>
                    <td>
                      <span style={{ background: MODE_COLORS[p.payment_mode] || '#718096', color: '#fff',
                        padding: '2px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{p.payment_mode}</span>
                    </td>
                    <td style={{ fontSize: 11, color: '#718096', whiteSpace: 'nowrap' }}>
                      {p.period_from ? `${fmtDate(p.period_from)} → ${p.period_to ? fmtDate(p.period_to) : '?'}` : '—'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#38a169' }}>{fmt(p.amount)}</td>
                    <td style={{ fontSize: 12, color: '#718096', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.notes || '—'}
                    </td>
                    <td>
                      {isOwner && (
                        <button className="btn" style={{ background: '#fed7d7', color: '#c53030', padding: '3px 10px', fontSize: 12 }}
                          onClick={() => del(p.id)}>Del</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {subTab === 'summary' && !loading && (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Employee</th><th>Trade</th><th style={{ textAlign: 'center' }}>Payments</th>
                <th style={{ textAlign: 'right' }}>Total Paid</th><th>Last Payment</th>
              </tr>
            </thead>
            <tbody>
              {summary.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: '#718096', padding: 24 }}>No payment data yet</td></tr>
              )}
              {summary.map((s, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 600 }}>{s.employee_name}</td>
                  <td>{s.trade || <span style={{ color: '#718096' }}>—</span>}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ background: '#ebf8ff', color: '#2b6cb0', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                      {s.payment_count}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#38a169' }}>{fmt(s.total_paid)}</td>
                  <td style={{ fontSize: 12, color: '#718096' }}>{fmtDate(s.last_payment)}</td>
                </tr>
              ))}
              {summary.length > 0 && (
                <tr style={{ borderTop: '2px solid var(--border-color)', fontWeight: 700 }}>
                  <td colSpan={3} style={{ textAlign: 'right', paddingRight: 16, color: '#4a5568' }}>Grand Total</td>
                  <td style={{ textAlign: 'right', color: '#38a169', fontSize: 15 }}>
                    {fmt(summary.reduce((s, r) => s + Number(r.total_paid || 0), 0))}
                  </td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ─── Supplier Payments tab ────────────────────────────────────────────────────
function SupplierPaymentsTab({ isOwner }) {
  const [payments,      setPayments]      = useState([]);
  const [summary,       setSummary]       = useState([]);
  const [invoices,      setInvoices]      = useState([]);
  const [allSupPay,     setAllSupPay]     = useState([]);
  const [supplierNames, setSupplierNames] = useState([]);
  const [form,          setForm]          = useState(EMPTY_SUP);
  const [showForm,      setShowForm]      = useState(false);
  const [subTab,        setSubTab]        = useState('history');
  const [filterSup,     setFilterSup]     = useState('');
  const [filterInv,     setFilterInv]     = useState('');
  const [fromDate,      setFromDate]      = useState('');
  const [toDate,        setToDate]        = useState('');
  const [loading,       setLoading]       = useState(true);
  const [err,           setErr]           = useState('');
  const [fe,            setFe]            = useState({});

  const inv = k => fe[k] ? { borderColor: '#e53e3e', background: '#fff5f5' } : {};

  const load = () => {
    setLoading(true);
    const params = {};
    if (filterSup) params.supplier  = filterSup;
    if (filterInv) params.invoiceNo = filterInv;
    if (fromDate)  params.from      = fromDate;
    if (toDate)    params.to        = toDate;
    Promise.all([
      builderApi.getSupplierPayments(params),
      builderApi.getSupplierPaymentSummary(),
      builderApi.getSupplierInvoices(),
      builderApi.getSupplierPayments({}),
    ]).then(([p, s, inv, allPay]) => {
      setPayments(p);
      setSummary(s);
      setInvoices(inv);
      setAllSupPay(allPay);
    }).catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  };

  // Load supplier names independently so a failing invoice/payment call can't prevent them loading
  useEffect(() => {
    builderApi.getSupplierNames()
      .then(sn => setSupplierNames((sn || []).map(r => r.supplier).filter(Boolean).sort((a, b) => a.localeCompare(b))))
      .catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, []); // eslint-disable-line

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const pickInvoice = (purchaseId) => {
    const rec = invoices.find(i => String(i.id) === String(purchaseId));
    if (rec) {
      const outstanding = Math.max(0, Number(rec.outstanding || 0));
      setForm(prev => ({
        ...prev,
        purchase_id: purchaseId,
        supplier:    rec.supplier   || prev.supplier,
        invoice_no:  rec.invoice_no || prev.invoice_no,
        paid_amount: outstanding > 0 ? outstanding.toFixed(2) : prev.paid_amount,
      }));
    } else {
      setForm(prev => ({ ...prev, purchase_id: purchaseId }));
    }
  };

  const selectedInvoice = invoices.find(i => String(i.id) === String(form.purchase_id));

  // Unique suppliers from invoices for autocomplete

  const save = async () => {
    const errors = {};
    if (!form.supplier.trim()) errors.supplier = true;
    if (!form.payment_date)    errors.payment_date = true;
    const paid   = Number(form.paid_amount   || 0);
    const credit = Number(form.credit_amount || 0);
    if (paid <= 0 && credit <= 0) errors.paid_amount = true;
    setFe(errors);
    if (Object.keys(errors).length > 0) { setErr('Supplier and at least one of paid/credit amount is required'); return; }
    try {
      await builderApi.createSupplierPayment(form);
      setShowForm(false); setForm(EMPTY_SUP); setErr(''); setFe({}); load();
    } catch (e) { setErr(e.message); }
  };

  const del = async id => {
    if (!window.confirm('Delete this supplier payment record?')) return;
    try { await builderApi.deleteSupplierPayment(id); load(); } catch (e) { setErr(e.message); }
  };

  const totalPaid   = payments.reduce((s, p) => s + Number(p.paid_amount   || 0), 0);
  const totalCredit = payments.reduce((s, p) => s + Number(p.credit_amount || 0), 0);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button className="btn btn-primary" onClick={() => { setForm(EMPTY_SUP); setShowForm(s => !s); }}>
          + Record Supplier Payment
        </button>
      </div>

      <ErrBanner msg={err} onClose={() => setErr('')} />

      {showForm && (
        <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', marginBottom: 12 }}>New Supplier Payment</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>

            {/* Pick from existing purchase invoice */}
            {invoices.length > 0 && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Link to Purchase Invoice (optional)</label>
                <select className="form-input" value={form.purchase_id} onChange={e => pickInvoice(e.target.value)}>
                  <option value="">— Select invoice to auto-fill —</option>
                  {invoices.map(i => {
                    const outstanding = Number(i.outstanding || 0);
                    return (
                      <option key={i.id} value={i.id}>
                        {i.supplier} | {i.invoice_no} | {fmtDate(i.purchase_date)} | Gross: {fmt(i.gross_amount)} | Outstanding: {fmt(outstanding)}
                      </option>
                    );
                  })}
                </select>
                {/* Invoice summary strip */}
                {selectedInvoice && (
                  <div style={{ marginTop: 8, background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                    borderRadius: 8, padding: '10px 14px', display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12 }}>
                    <div>
                      <div style={{ color: '#718096' }}>Gross Invoice</div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#3182ce' }}>{fmt(selectedInvoice.gross_amount)}</div>
                    </div>
                    {Number(selectedInvoice.total_sp_paid) > 0 && (
                      <div>
                        <div style={{ color: '#718096' }}>Prior Payments</div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#718096' }}>{fmt(selectedInvoice.total_sp_paid)}</div>
                      </div>
                    )}
                    <div>
                      <div style={{ color: '#718096' }}>Outstanding</div>
                      <div style={{ fontWeight: 700, fontSize: 14,
                        color: Number(selectedInvoice.outstanding) > 0 ? '#e53e3e' : '#38a169' }}>
                        {fmt(Math.max(0, Number(selectedInvoice.outstanding)))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="form-label">Supplier *</label>
              <input className="form-input" style={inv('supplier')} list="supplier-list" value={form.supplier}
                onChange={e => { f('supplier', e.target.value); if (fe.supplier) setFe(p => ({ ...p, supplier: false })); }}
                placeholder="Supplier name" />
              <datalist id="supplier-list">
                {supplierNames.map(s => <option key={s} value={s} />)}
              </datalist>
              {fe.supplier && <div style={{ color: '#e53e3e', fontSize: 11, marginTop: 3 }}>Required</div>}
            </div>

            <div>
              <label className="form-label">Invoice Number</label>
              <input className="form-input" value={form.invoice_no}
                onChange={e => f('invoice_no', e.target.value)} placeholder="e.g. INV-001" />
            </div>

            <div>
              <label className="form-label">Payment Date *</label>
              <DateInput className="form-input" style={inv('payment_date')} value={form.payment_date}
                onChange={e => { f('payment_date', e.target.value); if (fe.payment_date) setFe(p => ({ ...p, payment_date: false })); }} />
            </div>

            <div>
              <label className="form-label">Paid Amount (₹)</label>
              <input className="form-input" style={inv('paid_amount')} type="number" min="0" step="0.01"
                value={form.paid_amount} onChange={e => { f('paid_amount', e.target.value); setFe(p => ({ ...p, paid_amount: false })); }}
                placeholder="0.00" />
              {fe.paid_amount && <div style={{ color: '#e53e3e', fontSize: 11, marginTop: 3 }}>Enter paid or credit amount</div>}
            </div>

            <div>
              <label className="form-label">Credit Amount (₹)</label>
              <input className="form-input" type="number" min="0" step="0.01"
                value={form.credit_amount} onChange={e => f('credit_amount', e.target.value)} placeholder="0.00" />
              <div style={{ fontSize: 11, color: '#718096', marginTop: 3 }}>Discounts, returns, adjustments</div>
            </div>

            <div>
              <label className="form-label">Payment Mode</label>
              <select className="form-input" value={form.payment_mode} onChange={e => f('payment_mode', e.target.value)}>
                {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            <div style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Notes</label>
              <input className="form-input" value={form.notes}
                onChange={e => f('notes', e.target.value)} placeholder="Cheque no., reference, remarks…" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-primary" onClick={save}>Save</button>
            <button className="btn btn-outline" onClick={() => { setShowForm(false); setErr(''); }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border-color)', marginBottom: 16 }}>
        {[{ key: 'history', label: 'Payment History' }, { key: 'summary', label: 'Supplier Summary' }].map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '8px 20px', fontSize: 13, fontWeight: 600,
            color: subTab === t.key ? '#3182ce' : '#718096',
            borderBottom: subTab === t.key ? '2px solid #3182ce' : '2px solid transparent', marginBottom: -2,
          }}>{t.label}</button>
        ))}
      </div>

      {subTab === 'history' && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label className="form-label">Supplier</label>
            <select className="form-input" style={{ minWidth: 160 }} value={filterSup}
              onChange={e => setFilterSup(e.target.value)}>
              <option value="">All Suppliers</option>
              {supplierNames.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Invoice No</label>
            <input className="form-input" style={{ minWidth: 130 }} value={filterInv}
              onChange={e => setFilterInv(e.target.value)} placeholder="Invoice number" />
          </div>
          <div><label className="form-label">From</label><DateInput className="form-input" value={fromDate} onChange={e => setFromDate(e.target.value)} /></div>
          <div><label className="form-label">To</label><DateInput className="form-input" value={toDate} onChange={e => setToDate(e.target.value)} /></div>
          <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={load}>Apply</button>
          <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={() => { setFilterSup(''); setFilterInv(''); setFromDate(''); setToDate(''); }}>Clear</button>
        </div>
      )}

      {loading && <div style={{ color: '#718096', fontSize: 13, padding: 12 }}>Loading…</div>}

      {/* ── Supplier Account Summary (shown when a supplier is selected) ── */}
      {subTab === 'history' && !loading && filterSup && (() => {
        const supInvoices = invoices.filter(i => i.supplier === filterSup);
        const totalInvoiced   = supInvoices.reduce((s, i) => s + Number(i.gross_amount   || 0), 0);
        const totalSettled    = supInvoices.reduce((s, i) => s + Number(i.total_sp_paid  || 0), 0);
        const netOutstanding  = supInvoices.reduce((s, i) => s + Math.max(0, Number(i.outstanding || 0)), 0);
        return (
          <div style={{ background: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: 10,
            padding: '14px 18px', marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#2b6cb0', marginBottom: 12 }}>
              🏗️ {filterSup} — Account Summary
              {supInvoices.length > 0 && <span style={{ fontSize: 12, fontWeight: 500, marginLeft: 8 }}>({supInvoices.length} invoice{supInvoices.length !== 1 ? 's' : ''})</span>}
            </div>
            <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: supInvoices.length ? 14 : 0 }}>
              <div>
                <div style={{ fontSize: 11, color: '#718096', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>Total Invoiced</div>
                <div style={{ fontWeight: 800, fontSize: 18, color: '#3182ce' }}>{fmt(totalInvoiced)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#718096', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>Total Paid</div>
                <div style={{ fontWeight: 800, fontSize: 18, color: '#38a169' }}>{fmt(totalSettled)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#718096', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>Net Balance Due</div>
                <div style={{ fontWeight: 800, fontSize: 18, color: netOutstanding > 0 ? '#e53e3e' : '#38a169' }}>{fmt(netOutstanding)}</div>
              </div>
            </div>
            {supInvoices.length > 0 && (
              <div style={{ borderTop: '1px solid #bee3f8', paddingTop: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#718096', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>Invoice Breakdown</div>
                {supInvoices.map(inv => (
                  <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '5px 0', borderBottom: '1px solid rgba(190,227,248,0.6)', fontSize: 12 }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      {inv.invoice_no && (
                        <span style={{ fontFamily: 'monospace', background: '#2b6cb0', color: '#fff',
                          padding: '1px 7px', borderRadius: 4, fontSize: 11 }}>{inv.invoice_no}</span>
                      )}
                      <span style={{ color: '#718096' }}>{fmtDate(inv.purchase_date)}</span>
                      <span style={{ color: '#4a5568', fontWeight: 600 }}>Gross: {fmt(inv.gross_amount)}</span>
                      <span style={{ color: '#38a169' }}>Paid: {fmt(inv.total_sp_paid)}</span>
                      {inv.invoice_image && (
                        <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, padding: 0, color: '#3182ce' }}
                          title="View invoice image"
                          onClick={() => builderApi.getInvoiceUrl(inv.invoice_image).then(r => r?.url && window.open(r.url, '_blank'))}>
                          🖼️ View
                        </button>
                      )}
                    </div>
                    <span style={{ fontWeight: 700, flexShrink: 0, marginLeft: 12,
                      color: Number(inv.outstanding) > 0 ? '#e53e3e' : '#38a169' }}>
                      Due: {fmt(Math.max(0, Number(inv.outstanding || 0)))}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {subTab === 'history' && !loading && (
        <>
          {payments.length > 0 && (
            <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: '10px 16px', marginBottom: 12,
              display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, color: '#4a5568' }}>{payments.length} payment record(s)</span>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#38a169' }}>Paid: {fmt(totalPaid)}</span>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#3182ce' }}>Credit: {fmt(totalCredit)}</span>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#4a5568' }}>Net: {fmt(totalPaid + totalCredit)}</span>
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Supplier</th>
                  <th>Invoice No</th>
                  <th style={{ textAlign: 'right' }}>Invoice Amt</th>
                  <th style={{ textAlign: 'right' }}>Paid</th>
                  <th style={{ textAlign: 'right' }}>Credit</th>
                  <th>Mode</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: 'center', color: '#718096', padding: 24 }}>No supplier payments recorded yet</td></tr>
                )}
                {payments.map(p => (
                  <tr key={p.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(p.payment_date)}</td>
                    <td style={{ fontWeight: 600 }}>{p.supplier}</td>
                    <td>
                      {p.invoice_no
                        ? <span style={{ fontFamily: 'monospace', fontSize: 12, background: '#ebf8ff', color: '#2b6cb0', padding: '2px 7px', borderRadius: 8 }}>{p.invoice_no}</span>
                        : <span style={{ color: '#718096' }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'right', color: '#4a5568', fontSize: 13 }}>
                      {Number(p.invoice_amount) > 0 ? fmt(p.invoice_amount) : <span style={{ color: '#718096' }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#38a169' }}>
                      {Number(p.paid_amount) > 0 ? fmt(p.paid_amount) : <span style={{ color: '#718096' }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#3182ce' }}>
                      {Number(p.credit_amount) > 0 ? fmt(p.credit_amount) : <span style={{ color: '#718096' }}>—</span>}
                    </td>
                    <td>
                      <span style={{ background: MODE_COLORS[p.payment_mode] || '#718096', color: '#fff',
                        padding: '2px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{p.payment_mode}</span>
                    </td>
                    <td style={{ fontSize: 12, color: '#718096', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.notes || '—'}
                    </td>
                    <td>
                      {isOwner && (
                        <button className="btn" style={{ background: '#fed7d7', color: '#c53030', padding: '3px 10px', fontSize: 12 }}
                          onClick={() => del(p.id)}>Del</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {subTab === 'summary' && !loading && (() => {
        const allSuppliers = [...new Set([
          ...invoices.map(i => i.supplier),
          ...allSupPay.map(p => p.supplier),
        ])].filter(Boolean).sort((a, b) => a.localeCompare(b));

        if (allSuppliers.length === 0) {
          return <div style={{ textAlign: 'center', color: '#718096', padding: 32 }}>No supplier transactions yet</div>;
        }

        let grandInvoiced = 0, grandPaid = 0, grandBalance = 0;

        const supplierBlocks = allSuppliers.map(supplier => {
          const supInvoices = invoices.filter(i => i.supplier === supplier);
          const supPayments = allSupPay.filter(p => p.supplier === supplier);

          const entries = [
            ...supInvoices.map(i => ({
              date: i.purchase_date, sortKey: (i.purchase_date || '') + '_A_' + i.id,
              type: 'Invoice', ref: i.invoice_no,
              debit: Number(i.gross_amount || 0), credit: 0, notes: '', invoiceImage: i.invoice_image || '',
            })),
            ...supPayments.map(p => ({
              date: p.payment_date, sortKey: (p.payment_date || '') + '_B_' + p.id,
              type: 'Payment', ref: p.invoice_no || '',
              debit: 0, credit: Number(p.paid_amount || 0) + Number(p.credit_amount || 0),
              mode: p.payment_mode, notes: p.notes || '',
            })),
          ].sort((a, b) => a.sortKey < b.sortKey ? -1 : 1);

          let cumInvoiced = 0, cumPaid = 0;
          const rows = entries.map(e => {
            cumInvoiced += e.debit;
            cumPaid     += e.credit;
            return { ...e, cumInvoiced, cumPaid, balance: cumInvoiced - cumPaid };
          });

          const totalInvoiced = cumInvoiced;
          const totalPaid     = cumPaid;
          const netBalance    = cumInvoiced - cumPaid;

          grandInvoiced += totalInvoiced;
          grandPaid     += totalPaid;
          grandBalance  += netBalance;

          return { supplier, rows, totalInvoiced, totalPaid, netBalance };
        });

        return (
          <div>
            {supplierBlocks.map(({ supplier, rows, totalInvoiced, totalPaid, netBalance }) => (
              <div key={supplier} style={{ marginBottom: 28 }}>
                {/* Supplier header */}
                <div style={{ display: 'flex', gap: 24, alignItems: 'center', background: '#ebf8ff',
                  border: '1px solid #bee3f8', borderRadius: '8px 8px 0 0', padding: '10px 16px', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, fontSize: 15, color: '#2b6cb0', flex: 1, minWidth: 120 }}>{supplier}</span>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 10, color: '#718096', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>Total Invoiced</div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#3182ce' }}>{fmt(totalInvoiced)}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 10, color: '#718096', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>Total Paid</div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#38a169' }}>{fmt(totalPaid)}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 10, color: '#718096', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>Net Balance</div>
                    <div style={{ fontWeight: 800, fontSize: 15, color: netBalance > 0 ? '#e53e3e' : '#38a169' }}>
                      {fmt(Math.abs(netBalance))} <span style={{ fontSize: 11, fontWeight: 500 }}>{netBalance > 0 ? 'Due' : netBalance < 0 ? 'Overpaid' : 'Settled'}</span>
                    </div>
                  </div>
                </div>

                {/* Transaction ledger */}
                <div style={{ overflowX: 'auto', border: '1px solid #bee3f8', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
                  <table className="data-table" style={{ fontSize: 12, margin: 0 }}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Invoice No</th>
                        <th style={{ textAlign: 'right' }}>Invoice Amt (₹)</th>
                        <th style={{ textAlign: 'right' }}>Payment (₹)</th>
                        <th style={{ textAlign: 'right' }}>Cum. Invoiced (₹)</th>
                        <th style={{ textAlign: 'right' }}>Cum. Paid (₹)</th>
                        <th style={{ textAlign: 'right' }}>Balance (₹)</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 && (
                        <tr><td colSpan={9} style={{ textAlign: 'center', color: '#718096', padding: 16 }}>No transactions</td></tr>
                      )}
                      {rows.map((r, idx) => (
                        <tr key={idx} style={{ background: r.type === 'Invoice' ? 'rgba(255,243,205,0.25)' : 'inherit' }}>
                          <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</td>
                          <td>
                            <span style={{
                              background: r.type === 'Invoice' ? '#fef3c7' : '#d1fae5',
                              color:      r.type === 'Invoice' ? '#92400e' : '#065f46',
                              padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                            }}>{r.type}</span>
                          </td>
                          <td>
                            {r.ref
                              ? <span style={{ fontFamily: 'monospace', fontSize: 11, background: '#ebf8ff', color: '#2b6cb0', padding: '1px 6px', borderRadius: 4 }}>{r.ref}</span>
                              : <span style={{ color: '#718096' }}>—</span>}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: r.debit > 0 ? 700 : 400, color: r.debit > 0 ? '#e53e3e' : '#718096' }}>
                            {r.debit > 0 ? fmt(r.debit) : '—'}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: r.credit > 0 ? 700 : 400, color: r.credit > 0 ? '#38a169' : '#718096' }}>
                            {r.credit > 0 ? fmt(r.credit) : '—'}
                          </td>
                          <td style={{ textAlign: 'right', color: '#4a5568' }}>{fmt(r.cumInvoiced)}</td>
                          <td style={{ textAlign: 'right', color: '#4a5568' }}>{fmt(r.cumPaid)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap',
                            color: r.balance > 0 ? '#e53e3e' : '#38a169' }}>
                            {fmt(Math.abs(r.balance))}
                            <span style={{ fontSize: 10, marginLeft: 3, fontWeight: 400 }}>
                              {r.balance > 0 ? 'Dr' : r.balance < 0 ? 'Cr' : ''}
                            </span>
                          </td>
                          <td style={{ fontSize: 11, color: '#718096' }}>
                            {r.notes || '—'}
                            {r.invoiceImage && (
                              <button style={{ marginLeft: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#3182ce', padding: 0 }}
                                onClick={() => builderApi.getInvoiceUrl(r.invoiceImage).then(res => res?.url && window.open(res.url, '_blank'))}>
                                🖼️ View
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            {/* Grand totals row */}
            <div style={{ display: 'flex', gap: 24, alignItems: 'center', background: 'var(--bg-secondary)',
              border: '2px solid var(--border-color)', borderRadius: 8, padding: '12px 16px', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', flex: 1 }}>Grand Total — All Suppliers</span>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: '#718096', fontWeight: 600, textTransform: 'uppercase' }}>Total Invoiced</div>
                <div style={{ fontWeight: 800, fontSize: 15, color: '#3182ce' }}>{fmt(grandInvoiced)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: '#718096', fontWeight: 600, textTransform: 'uppercase' }}>Total Paid</div>
                <div style={{ fontWeight: 800, fontSize: 15, color: '#38a169' }}>{fmt(grandPaid)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: '#718096', fontWeight: 600, textTransform: 'uppercase' }}>Net Balance Due</div>
                <div style={{ fontWeight: 800, fontSize: 16, color: grandBalance > 0 ? '#e53e3e' : '#38a169' }}>{fmt(Math.max(0, grandBalance))}</div>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function BuilderPayments({ user }) {
  const [tab, setTab] = useState('worker');
  const isOwner = user?.role === 'role_owner';

  const TABS = [
    { key: 'worker',   label: '👷 Worker Payments' },
    { key: 'supplier', label: '🏗️ Supplier Payments' },
  ];

  return (
    <div className="bld-card">
      <h2 style={{ margin: '0 0 16px', fontSize: 18, color: 'var(--text-primary)' }}>Payments</h2>

      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border-color)', marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '10px 24px', fontSize: 14, fontWeight: 600,
            color: tab === t.key ? '#3182ce' : '#718096',
            borderBottom: tab === t.key ? '2px solid #3182ce' : '2px solid transparent', marginBottom: -2,
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'worker'   && <EmployeePaymentsTab isOwner={isOwner} />}
      {tab === 'supplier' && <SupplierPaymentsTab isOwner={isOwner} />}
    </div>
  );
}
